import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { AutoRefreshToolbar } from '@/components/common';
import { ErrorBanner } from '@/components/ui';
import { templatesService, type SearchTemplate } from '@/api/services/templates';
import { useMe } from '@/context/MeContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useToast } from '@/components/Toast';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import { formatApiError } from '@/utils/formatApiError';
import {
  canBrowseTemplates,
  canFavoriteTemplates,
  canImportTemplates,
  canInstallTemplates,
  canManageOrganizationTemplates,
} from '@/utils/navCapabilities';

function TemplateCard({
  template,
  canInstall,
}: {
  template: SearchTemplate;
  canInstall: boolean;
}) {
  return (
    <article className="card card-elevated template-card">
      <div className="template-card__header">
        <h3>{template.title}</h3>
        {template.verified ? <span className="badge badge-success">Verified</span> : null}
        {template.featured ? <span className="badge badge-info">Featured</span> : null}
        {template.recommended ? <span className="badge">Recommended</span> : null}
      </div>
      <p className="muted">{template.shortDescription}</p>
      <p className="template-card__meta">
        {template.category} · v{template.version} · {template.installCount ?? 0} installs
      </p>
      {template.marketplaces && template.marketplaces.length > 0 ? (
        <p className="muted template-card__markets">{template.marketplaces.join(', ')}</p>
      ) : null}
      <div className="template-card__actions">
        <Link to={`/templates/${template.id}`} className="btn btn-sm btn-ghost">
          Preview
        </Link>
        {canInstall ? (
          <Link to={`/templates/install?id=${template.id}`} className="btn btn-sm">
            Install
          </Link>
        ) : null}
      </div>
    </article>
  );
}

function TemplateSection({
  title,
  items,
  canInstall,
}: {
  title: string;
  items: SearchTemplate[];
  canInstall: boolean;
}) {
  if (!items.length) return null;
  return (
    <section className="template-section">
      <h2>{title}</h2>
      <div className="template-grid">{items.map((t) => <TemplateCard key={t.id} template={t} canInstall={canInstall} />)}</div>
    </section>
  );
}

export default function TemplatesMarketplacePage() {
  const { me } = useMe();
  const { isPersonalMode } = useWorkspace();
  const { toast } = useToast();
  const navigate = useNavigate();
  const importInputRef = useRef<HTMLInputElement>(null);
  const canBrowse = canBrowseTemplates(me);
  const canInstall = canInstallTemplates(me);
  const canFavorite = canFavoriteTemplates(me);
  const canManageOrg = canManageOrganizationTemplates(me);
  const canImport = isPersonalMode
    ? canImportTemplates(me) && canInstallTemplates(me)
    : canManageOrg;
  const [featured, setFeatured] = useState<SearchTemplate[]>([]);
  const [recommended, setRecommended] = useState<SearchTemplate[]>([]);
  const [popular, setPopular] = useState<SearchTemplate[]>([]);
  const [recent, setRecent] = useState<SearchTemplate[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('popular');
  const [error, setError] = useState('');

  const hasTemplateData = useCallback(
    () => featured.length + recommended.length + popular.length + recent.length > 0,
    [featured, recommended, popular, recent],
  );

  const fetchTemplates = useCallback(async () => {
    const [catRes, feat, rec, pop, list] = await Promise.all([
      templatesService.categories(),
      templatesService.featured(),
      templatesService.recommended(),
      templatesService.popular(),
      templatesService.list({
        q: query || undefined,
        category: category || undefined,
        sort,
        pageSize: 12,
      }),
    ]);
    setCategories(catRes.items);
    setFeatured(feat.items);
    setRecommended(rec.items);
    setPopular(pop.items);
    setRecent(list.items);
    setError('');
  }, [query, category, sort]);

  const { autoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: hasTemplateData,
    load: async () => {
      try {
        await fetchTemplates();
      } catch (e) {
        setError(formatApiError(e, 'Failed to load templates'));
        throw e;
      }
    },
    autoRefresh: {
      intervalMs: 60000,
      deps: [query, category, sort, canBrowse],
      storageKey: 'templates-marketplace-auto-refresh',
      enabled: canBrowse,
    },
  });

  const reload = useCallback(async () => {
    await autoRefresh.refresh();
  }, [autoRefresh]);

  async function handleImportFile(file: File) {
    setError('');
    try {
      const text = await file.text();
      const payload = JSON.parse(text) as Record<string, unknown>;
      await templatesService.import(payload);
      toast('Template imported', 'success');
      navigate('/templates/my', { state: { refresh: Date.now() } });
    } catch (e) {
      setError(formatApiError(e, 'Import failed — check that the JSON export is valid'));
    }
  }

  if (!canBrowse) {
    return (
      <>
        <PageHeader title="Templates" subtitle="Browse reusable search templates" />
        <p className="muted">You do not have permission to browse templates.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Templates" subtitle="Install professionally built searches with one click">
        {canFavorite ? (
          <Link to="/templates/favorites" className="btn btn-ghost btn-sm">
            Favorites
          </Link>
        ) : null}
        {canInstall ? (
          <Link to="/templates/my" className="btn btn-ghost btn-sm">
            My templates
          </Link>
        ) : null}
        {canInstall ? (
          <Link to="/templates/history" className="btn btn-ghost btn-sm">
            Install history
          </Link>
        ) : null}
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 60s" />
        {canImport ? (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => importInputRef.current?.click()}
            >
              Import JSON
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImportFile(file);
                e.target.value = '';
              }}
            />
          </>
        ) : null}
      </PageHeader>

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      <div className="template-toolbar">
        <input
          className="input"
          placeholder="Search templates…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void reload();
          }}
        />
        <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select className="input" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="popular">Popular</option>
          <option value="newest">Newest</option>
          <option value="updated">Recently updated</option>
          <option value="featured">Featured</option>
        </select>
        <button type="button" className="btn btn-sm" onClick={() => void reload()}>
          Search
        </button>
      </div>

      {initialLoading && !hasTemplateData() ? <p className="muted">Loading templates…</p> : null}

      {!query && !category ? (
        <>
          <TemplateSection title="Featured" items={featured} canInstall={canInstall} />
          <TemplateSection title="Recommended" items={recommended} canInstall={canInstall} />
          <TemplateSection title="Popular" items={popular} canInstall={canInstall} />
        </>
      ) : null}

      <TemplateSection title={query || category ? 'Results' : 'Recently updated'} items={recent} canInstall={canInstall} />
    </>
  );
}
